import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { color, priorityTheme } from '../styles/theme';
import { fullDate, siteDay } from '../lib/format';
import { IconSearch } from '../components/icons';
import { useAuth } from '../auth/AuthContext';
import type { MeetingItem, TicketPriority, WorkStatus, WorkTicket } from '../api/client';
import { useOpenActions, useUpdateItem } from '../api/meetings';
import { PRIORITY_LABEL, WORK_STATUS_LABEL, useUpdateTicket, useWorkTickets, type TicketListView } from '../api/tickets';
import { SectionTabs } from '../components/SectionTabs';
import { Button, Card, ColHead, ErrorNote, Loading } from '../components/ui';
import { DueTag, isLate } from './MeetingBits';

/**
 * My tickets: the two kinds of work in one list.
 *
 * Tickets come from /work/tickets, action items from /meetings/actions; the
 * rows are merged here and sorted by due date, undated last (closed ones by
 * when they closed). A ticket's row opens the ticket; an action item's row
 * opens its meeting, where it is settled. A ticket's Done button shows for
 * whoever may close it: the queue (tickets:update), the person who filed it,
 * and the person it is assigned to — matched by account id, never by name.
 */

const GRID = '112px minmax(0,2.4fr) minmax(120px,1fr) 96px 92px 118px 84px 96px 96px';
const PAGE_SIZE = 200;

interface Row {
  key: string;
  kind: 'ticket' | 'action';
  id: string;
  ref: string;
  title: string;
  from: string;
  requester: string;
  /** Who filed it — null for an action item, which is settled through its meeting. */
  requesterId: string | null;
  priority: TicketPriority | null;
  status: WorkStatus;
  ownerId: string | null;
  ownerName: string | null;
  dueOn: string | null;
  createdAt: string;
  closedAt: string | null;
  meetingId: string | null;
}

const fromTicket = (t: WorkTicket): Row => ({
  key: `t:${t.id}`,
  kind: 'ticket',
  id: t.id,
  ref: t.ref,
  title: t.title,
  from: t.source?.type === 'redcap_flag' ? `REDCap${t.studySlug ? ` · ${t.studySlug}` : ''}` : t.studySlug ? `Study · ${t.studySlug}` : 'Filed',
  requester: t.requesterName ?? '—',
  requesterId: t.requesterId,
  priority: t.priority,
  status: t.status,
  ownerId: t.ownerId,
  ownerName: t.ownerName,
  dueOn: t.dueOn,
  createdAt: t.createdAt,
  closedAt: t.closedAt,
  meetingId: null,
});

const fromAction = (a: MeetingItem): Row => ({
  key: `a:${a.id}`,
  kind: 'action',
  id: a.id,
  ref: 'Meeting',
  title: a.body,
  from: a.scheduledAt ? `${a.meetingTitle} · ${siteDay(a.scheduledAt)}` : a.meetingTitle,
  requester: a.authorName ?? '—',
  requesterId: null,
  priority: null,
  status: a.status ?? 'open',
  ownerId: a.ownerId,
  ownerName: a.ownerName,
  dueOn: a.dueOn,
  createdAt: a.createdAt,
  closedAt: a.closedAt,
  meetingId: a.meetingId,
});

/** Whole days since it was filed, until it closed. */
function daysOpen(r: Row): number {
  const end = r.closedAt ? new Date(r.closedAt).getTime() : Date.now();
  return Math.max(0, Math.floor((end - new Date(r.createdAt).getTime()) / 86_400_000));
}

const VIEWS: ReadonlyArray<{ key: TicketListView; label: string }> = [
  { key: 'mine', label: 'My action items' },
  { key: 'requested', label: 'My requests' },
  { key: 'open', label: 'All open' },
  { key: 'closed', label: 'Closed' },
];

export function TicketList({
  onOpenTicket,
  onOpenMeeting,
}: {
  onOpenTicket: (id: string) => void;
  onOpenMeeting: (meetingId: string) => void;
}) {
  const { can, admin } = useAuth();
  const canRunQueue = can('tickets:update');
  const canSettleActions = can('meetings:update');
  const canSeeActions = can('meetings:read');
  const [view, setView] = useState<TicketListView>('mine');
  const [q, setQ] = useState('');
  const updateTicket = useUpdateTicket();
  const updateItem = useUpdateItem();

  useEffect(() => setQ(''), [view]);

  const tickets = useWorkTickets({ view, ...(q.trim() ? { q: q.trim() } : {}), pageSize: PAGE_SIZE });
  const actions = useOpenActions(
    { owner: view === 'mine' ? 'me' : 'all', state: view === 'closed' ? 'closed' : 'open' },
    canSeeActions && view !== 'requested',
  );
  const lateMine = useOpenActions({ owner: 'me', state: 'late' }, canSeeActions);
  const mineTickets = useWorkTickets({ view: 'mine', pageSize: PAGE_SIZE });

  const rows = useMemo<Row[]>(() => {
    const needle = q.trim().toLowerCase();
    const fromTickets = (tickets.data?.rows ?? []).map(fromTicket);
    const fromActions = (actions.data?.actions ?? [])
      .map(fromAction)
      .filter((r) => !needle || r.title.toLowerCase().includes(needle) || r.from.toLowerCase().includes(needle));
    const all = [...fromTickets, ...fromActions];
    if (view === 'closed') {
      return all.sort((a, b) => (b.closedAt ?? b.createdAt).localeCompare(a.closedAt ?? a.createdAt));
    }
    return all.sort((a, b) => {
      if (a.dueOn !== b.dueOn) {
        if (a.dueOn === null) return 1;
        if (b.dueOn === null) return -1;
        return a.dueOn < b.dueOn ? -1 : 1;
      }
      return a.createdAt.localeCompare(b.createdAt);
    });
  }, [tickets.data, actions.data, view, q]);

  const late = rows.filter((r) => r.status === 'open' && isLate({ dueOn: r.dueOn, status: r.status })).length;
  const unowned = rows.filter((r) => r.status === 'open' && !r.ownerId).length;
  const mineLate =
    (lateMine.data?.actions.length ?? 0) +
    (mineTickets.data?.rows ?? []).filter((t) => isLate({ dueOn: t.dueOn, status: t.status })).length;

  const loading = (tickets.isLoading && !tickets.data) || (actions.isLoading && !actions.data && actions.isEnabled !== false);
  const error = tickets.error ?? (canSeeActions ? actions.error : null);

  const me = admin?.id ?? null;
  const canSettle = (r: Row): boolean =>
    r.kind === 'ticket'
      ? canRunQueue || (me !== null && (r.requesterId === me || r.ownerId === me))
      : canSettleActions;

  const settle = (r: Row) => {
    if (r.kind === 'ticket') updateTicket.mutate({ id: r.id, status: 'done' });
    else if (r.meetingId) updateItem.mutate({ id: r.meetingId, itemId: r.id, status: 'done' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SectionTabs
        value={view}
        onChange={setView}
        compact
        tabs={VIEWS.map((v) => ({ key: v.key, label: v.label, ...(v.key === 'mine' && mineLate > 0 ? { badge: mineLate } : {}) }))}
      />

      <Card pad={0} style={{ overflow: 'hidden' }}>
        <div style={toolbar}>
          <div style={searchBox}>
            <span style={{ color: color.muted, display: 'flex', flex: 'none' }}>
              <IconSearch size={15} />
            </span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Title or ticket number"
              aria-label="Search tickets by title or number"
              style={{ border: 'none', outline: 'none', fontSize: 13, color: color.ink, flex: 1, background: 'transparent', minWidth: 0 }}
            />
          </div>
          <span style={{ fontSize: 13, color: color.muted }}>
            <strong style={{ color: color.ink }}>{rows.length}</strong> {rows.length === 1 ? 'item' : 'items'}
          </span>
          <span style={{ flex: 1 }} />
          {late > 0 && <span style={{ ...badge, background: '#fdf1ee', color: '#a4432f' }}>{late} overdue or due today</span>}
          {unowned > 0 && <span style={{ ...badge, background: '#f6ddc2', color: '#8a4f16' }}>{unowned} need an owner</span>}
        </div>

        <div className="x-scroll">
          <div style={{ minWidth: 1040 }}>
            <div style={{ ...gridRow, padding: '10px 20px', borderBottom: `1px solid ${color.line}` }}>
              <ColHead>Ticket</ColHead>
              <ColHead>Title</ColHead>
              <ColHead>Requester</ColHead>
              <ColHead>Priority</ColHead>
              <ColHead>Status</ColHead>
              <ColHead>Owner</ColHead>
              <ColHead>Due</ColHead>
              <ColHead>Days open</ColHead>
              <ColHead>Filed</ColHead>
            </div>

            {loading ? (
              <Loading />
            ) : error ? (
              <ErrorNote message={(error as Error).message} />
            ) : rows.length === 0 ? (
              <p style={{ padding: '28px 20px', fontSize: 14, color: color.muted, margin: 0 }}>
                {q.trim()
                  ? 'Nothing matches.'
                  : view === 'mine'
                    ? 'Nothing is assigned to you. Action items from meetings and tickets given to you appear here.'
                    : view === 'requested'
                      ? 'You have not filed a ticket yet.'
                      : view === 'closed'
                        ? 'Nothing has been closed yet.'
                        : 'Nothing is open.'}
              </p>
            ) : (
              rows.map((r) => (
                <div key={r.key} className="hov-row" style={{ ...gridRow, padding: '11px 20px', borderBottom: `1px solid ${color.line}`, fontSize: 13.5 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: r.kind === 'ticket' ? color.blueDeep : color.muted, whiteSpace: 'nowrap' }}>
                    {r.ref}
                  </span>
                  <button
                    onClick={() => (r.kind === 'ticket' ? onOpenTicket(r.id) : r.meetingId && onOpenMeeting(r.meetingId))}
                    title={r.kind === 'ticket' ? 'Open the ticket' : 'Open the meeting it was raised in'}
                    style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', minWidth: 0 }}
                  >
                    <span style={{ display: 'block', fontWeight: 700, color: color.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.title}
                    </span>
                    <span style={{ display: 'block', fontSize: 11.5, color: color.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.from}
                    </span>
                  </button>
                  <span style={{ fontSize: 12.5, color: color.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.requester}</span>
                  <span>{r.priority ? <PriorityPill priority={r.priority} /> : <span style={{ fontSize: 12.5, color: color.muted }}>—</span>}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: r.status === 'open' ? color.ink : color.muted }}>{WORK_STATUS_LABEL[r.status]}</span>
                  <span style={{ fontSize: 12.5, color: r.ownerName ? color.ink : '#8a4f16', fontWeight: r.ownerName ? 400 : 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.ownerName ?? (r.status === 'open' ? 'Needs an owner' : '—')}
                  </span>
                  <DueTag dueOn={r.dueOn} status={r.status} />
                  <span style={{ fontSize: 12.5, color: color.muted }}>{daysOpen(r)}</span>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 12.5, color: color.muted, whiteSpace: 'nowrap' }}>{fullDate(r.createdAt)}</span>
                    {r.status === 'open' && canSettle(r) && (
                      <Button variant="soft" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => settle(r)} disabled={updateTicket.isPending || updateItem.isPending} title="Mark done">
                        Done
                      </Button>
                    )}
                  </span>
                </div>
              ))
            )}
            {(updateTicket.error || updateItem.error) && (
              <div style={{ padding: '0 20px 12px' }}>
                <ErrorNote message={((updateTicket.error ?? updateItem.error) as Error).message} />
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export function PriorityPill({ priority }: { priority: TicketPriority }) {
  const t = priorityTheme[priority];
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        padding: '3px 9px',
        borderRadius: 999,
        background: t.bg,
        color: t.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {PRIORITY_LABEL[priority]}
    </span>
  );
}

const gridRow: CSSProperties = { display: 'grid', gridTemplateColumns: GRID, gap: 12, alignItems: 'center' };

const toolbar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  padding: '14px 20px',
  borderBottom: `1px solid ${color.line}`,
};

const searchBox: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: color.bg,
  borderRadius: 999,
  padding: '9px 16px',
  width: 'min(260px,50vw)',
};

const badge: CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  padding: '3px 9px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
};
